// IMOTEpS — Centre de recherche / Chapitre 2
// Auto-split from chapters.js (Feb 2026 refactor)

export const chapter02 = {
  id: 2,
  slug: "arthropathie-acromio-claviculaire",
  color: "#2E7D6B",
  title: "Arthropathie acromio-claviculaire",
  subtitle: "Une pathologie fréquente, sous-diagnostiquée, et bien traitée chirurgicalement",
  region: "Épaule",
  readingMin: 18,
  summary:
    "Arthrose vraie ou ostéolyse de la clavicule distale (musculation, sportifs overhead, travailleurs manuels). Diagnostic clinique : douleur localisée au sommet de l'épaule, palpation directe AC (Se 96 %), test d'adduction horizontale croisée, test d'O'Brien. Infiltration AC écho-guidée en 1ère ligne. Résection distale de clavicule (Mumford) — arthroscopique préférée — > 90 % bons résultats.",
  sections: [
    {
      heading: "1. Une pathologie fréquente, sous-diagnostiquée",
      body: "Recouvre deux entités : l'arthrose AC (dégénérative, > 50 ans) et l'ostéolyse de la clavicule distale (micro-fractures de stress liées aux contraintes répétées). Terrains classiques : musculation/haltérophilie (développé couché, dips), nageurs de haut niveau, travailleurs manuels (charpentiers, peintres), sports overhead, post-traumatique (entorse AC séquellaire).",
    },
    {
      heading: "2. Diagnostic clinique — l'examen prime",
      body: "Douleur bien localisée au sommet de l'épaule, exactement sur l'AC. Elle ne descend PAS dans le deltoïde (≠ pathologie sous-acromiale) et n'irradie PAS en trapèze (≠ cervical). Déclenchée en fin d'élévation > 140-150°, en adduction horizontale croisée, au développé couché ou au crawl.",
    },
    {
      heading: "3. Tests cliniques — niveaux de preuve",
      list: [
        "Palpation directe AC : Se 96 %, Sp 10 % — test le plus sensible isolément",
        "Adduction horizontale croisée : Se 77 %, Sp 79 %",
        "Test d'O'Brien (active compression) : Se 41 %, Sp 95 % — douleur « en haut » = AC, « dedans » = labrum",
        "Signe de Paxinos : Se 79 %",
        "Test SAC (Saccomanni) : Se 98 %, Sp 92 %",
        "Infiltration diagnostique AC écho-guidée : gold standard",
      ],
    },
    {
      heading: "4. Imagerie",
      body: "Radiographie incidence de Zanca (10-15° crâniale) : pincement, ostéophytes, ostéolyse — Sp 90 %, Se seulement 40 %. Échographie : épanchement, ostéophytose, guide les infiltrations (l'injection sans guidage écho est intra-articulaire dans seulement 50-70 % des cas). IRM : gold standard pour l'ostéolyse de la clavicule distale (œdème médullaire précoce avant la radio).",
    },
    {
      heading: "5. Traitement conservateur",
      list: [
        "Repos sportif relatif, modification d'activité, adaptation technique",
        "AINS courts en cas de pic douloureux",
        "Infiltration intra-articulaire AC écho-guidée — traitement de référence en 1ère ligne (valeur diagnostique et thérapeutique)",
        "Kinésithérapie : renforcement péri-scapulaire pour décharger l'AC",
        "Acide hyaluronique : 2e ligne, données limitées dans l'AC",
      ],
    },
    {
      heading: "6. Résection distale de la clavicule (opération de Mumford)",
      body: "Décrite par Mumford et Gurd en 1941. Aujourd'hui 69-80 % des cas par voie arthroscopique. Résection de 7 à 10 mm — pas plus, pour préserver les ligaments coraco-claviculaires et éviter l'instabilité secondaire. Mini-abord direct : rapide, ~3 cm, retour au travail rapide. Arthroscopie : préserve les ligaments AC supérieurs, explore et traite les pathologies associées de coiffe ou bourrelet, moins d'infections (0,3 % vs 1,9 %), moins d'hématomes.",
    },
    {
      heading: "7. Résultats publiés",
      list: [
        "Pensak 2010 (revue 17 études) : > 90 % bons à excellents résultats, arthroscopique = retour aux activités plus rapide",
        "Forlenza 2021 (11 228 patients) : moins d'infections, hématomes, désunions en arthroscopique",
        "JAAOS Global Research 2024 : 82 % satisfaits ou très satisfaits à 2 ans",
        "Méta-analyse 4 études (319 pts) : équivalence fonctionnelle Constant/ASES/SST/VAS",
      ],
    },
    {
      heading: "8. Complications à connaître",
      body: "Infection 0,3-1,9 %, hématome 0,2-0,9 %, troubles cutanés transitoires (30-50 %), cicatrice hypertrophique sur terrain prédisposant, instabilité secondaire si résection excessive (> 10 mm) ou violation des ligaments coraco-claviculaires, résection insuffisante (5-10 % — douleur persistante), calcifications sur la tranche de résection, capsulite post-opératoire.",
    },
  ],
  references: [
    { id: 1, text: "Pensak M, et al. Open vs arthroscopic distal clavicle resection: systematic review. 2010." },
    { id: 2, text: "Forlenza EM, et al. Open vs arthroscopic distal clavicle excision: 11 228 patients. JSES Int. 2021." },
    { id: 3, text: "Chronopoulos E, et al. Diagnostic value of physical tests for AC joint disease. AJSM. 2004.", pubmed: "15090396" },
    { id: 4, text: "O'Brien SJ, et al. The active compression test. AJSM. 1998;26(5):610-3.", pubmed: "9784805" },
    { id: 5, text: "JAAOS Global Research 2024 — Patient satisfaction after arthroscopic distal clavicle resection." },
  ],
};
